/**
 * Delivery-size helper.
 *
 * Own Object Storage originals are served exactly as stored — there is no
 * on-the-fly resize/transform API, so the pre-rendered `thumb_url` is used
 * for the 'thumb' preset when the caller passes the full asset/image_ref
 * object. Legacy Cloudinary delivery URLs (pre-Sep-2026 migration) still get
 * their old transformation applied, though the account itself is now
 * deactivated so those links no longer resolve.
 */

export const MEDIA_PRESETS = {
  thumb: 'c_fill,g_auto,w_160,h_160',
  avatar: 'c_fill,g_face,w_400,h_400',
  card: 'c_limit,w_600',
  mobile: 'c_limit,w_800',
  hero: 'c_limit,w_1600',
  full: 'c_limit,w_2000',
};

const AUTO = 'f_auto,q_auto';

/** The raw delivery URL for a reference or legacy value, untransformed. */
export const mediaSourceUrl = (refOrUrl) => {
  if (!refOrUrl) return '';
  if (typeof refOrUrl === 'string') return refOrUrl;
  return refOrUrl.url || refOrUrl.secure_url || '';
};

/**
 * A transformed delivery URL. Non-Cloudinary URLs are returned untouched
 * (except the 'thumb' preset, which prefers thumb_url when available), and a
 * Cloudinary URL that already carries a transformation is left alone.
 */
export const mediaUrl = (refOrUrl, preset = 'card') => {
  if (typeof refOrUrl === 'object' && refOrUrl && preset === 'thumb' && refOrUrl.thumb_url) {
    return refOrUrl.thumb_url;
  }
  const url = mediaSourceUrl(refOrUrl);
  if (!url || !url.includes('/image/upload/')) return url;

  const transformation = MEDIA_PRESETS[preset] || MEDIA_PRESETS.card;
  const [prefix, rest] = url.split('/image/upload/');

  // Already transformed (a segment before the version that isn't v123/public id)
  const firstSegment = rest.split('/')[0];
  if (/^[a-z]{1,3}_[^/]+/.test(firstSegment)) return url;

  return `${prefix}/image/upload/${AUTO},${transformation}/${rest}`;
};

/** Reads the canonical reference first, then the legacy URL fields. */
export const resolveImage = (record, refField, ...legacyFields) => {
  if (!record) return '';
  const ref = record[refField];
  if (ref && (ref.url || ref.public_id)) return ref;
  for (const field of legacyFields) {
    const value = record[field];
    if (Array.isArray(value) && value.length) return value[0];
    if (value) return value;
  }
  return '';
};
